import { ResourceType } from "@common/types/auditLog";
import {
    APIProcessName,
    RPCProcessName,
    SQSProcessName,
} from "../process-names";
import { HistoryField, HistoryGroupBy } from "./history-fields";
import type {
    HistorySourceTypeConfig,
    ResourceTypeConfig,
} from "./history-source-config";
import type { PlatformHistoryTwinsBySourceType } from "../history-platform-twins";
import { toPlatformTwinSources } from "../history-platform-twins";
import { DOCUMENT_RESOURCE_TYPE_CONFIGS } from "./source-fields";
import { TRANSACTION_FIELDS } from "./transaction-sources";

const DOCUMENT_CREATE_DELETE_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType =
    {
        [APIProcessName.FINANCIAL_DOCUMENT_UPLOAD_DOCUMENT]: null,
        [APIProcessName.FINANCIAL_DOCUMENT_DELETE_DOCUMENT]: null,
        [APIProcessName.FINANCIAL_DOCUMENT_PROCESS_ARCHIVE]: null,
        [APIProcessName.FINANCIAL_DOCUMENT_BULK_ACTION]: null,
        [APIProcessName.EXPORT_SAVE_DOCUMENT]: null,
    };

const DOCUMENT_DOWNLOAD_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType = {
    [APIProcessName.FINANCIAL_DOCUMENT_DOWNLOAD_FILE]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_MCP_DOWNLOAD]: null,
    [SQSProcessName.DOCUMENT_DOWNLOAD]: null,
};

const DOCUMENT_FIELD_UPDATE_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType = {
    [APIProcessName.FINANCIAL_DOCUMENT_SCAN_DOCUMENT]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_RUN_LOAN_EXTRACTION]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_ADD_TO_IMPORTER]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_CHANGE_ENTITY]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_EXTRACT_ACCOUNT_INFO]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_TAXONOMY_EXTRACTION]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_REMOVE_IMPORTER_ERROR]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_TRANSACTIONS_IMPORT]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_INVOICES_IMPORT]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_FINALIZE_BILLS_IMPORT]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_CHECK_FOR_IMPORTER_ERRORS]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_REMOVE_FROM_IMPORTER]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_RUN_TAXONOMY_EXTRACTION]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_DOCUMENT_CATEGORY]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_ACCOUNT_INFO]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_UPDATE_DOCUMENT_METADATA]: null,
    [SQSProcessName.FINANCIAL_DOCUMENT_ACCRUAL_IMPORT]: null,
    [SQSProcessName.EXTRACT_ACCOUNT_INFO_FROM_DOCUMENT]: null,
    [SQSProcessName.DOCUMENT_SCANNING]: null,
    [SQSProcessName.REVALIDATE_IMPORTER_DOCUMENTS]: null,
    [RPCProcessName.FINANCIAL_DOCUMENT_UPLOADED]: null,
    [RPCProcessName.FINANCIAL_DOCUMENT_RETRY_MATCHING]: null,
    [RPCProcessName.MATCH_FINANCIAL_DOCUMENT]: null,
};

export const documentSources: HistorySourceTypeConfig[] = [
    ...toPlatformTwinSources(
        DOCUMENT_CREATE_DELETE_PLATFORM_TWINS,
    ).map<HistorySourceTypeConfig>(({ sourceType, platformTwins }) => ({
        sourceType,
        resourceTypeConfigs: DOCUMENT_RESOURCE_TYPE_CONFIGS,
        groupBy: HistoryGroupBy.NONE,
        platformTwins,
        enabled: true,
    })),
    ...toPlatformTwinSources(
        DOCUMENT_DOWNLOAD_PLATFORM_TWINS,
    ).map<HistorySourceTypeConfig>(({ sourceType, platformTwins }) => ({
        sourceType,
        resourceTypeConfigs: DOCUMENT_RESOURCE_TYPE_CONFIGS,
        groupBy: HistoryGroupBy.NONE,
        platformTwins,
        enabled: true,
    })),
    ...toPlatformTwinSources(
        DOCUMENT_FIELD_UPDATE_PLATFORM_TWINS,
    ).map<HistorySourceTypeConfig>(({ sourceType, platformTwins }) => ({
        sourceType,
        resourceTypeConfigs: DOCUMENT_RESOURCE_TYPE_CONFIGS,
        groupBy: HistoryGroupBy.FIELD,
        platformTwins,
        enabled: true,
    })),
    {
        sourceType: SQSProcessName.FINANCIAL_DOCUMENT_TRANSACTIONS_IMPORT,
        resourceTypeConfigs: [
            ...DOCUMENT_RESOURCE_TYPE_CONFIGS,
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
];

const RECEIPT_MATCH_RESOURCE_CONFIG: ResourceTypeConfig = {
    resourceType: ResourceType.TRANSACTION,
    fields: [HistoryField.FINANCIAL_DOCUMENT_ID],
};

const RECEIPT_MATCH_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType = {
    [APIProcessName.TRANSACTION_CREATE_LINK]: null,
    [APIProcessName.FINANCIAL_DOCUMENT_CREATE_LINK]: null,
    [APIProcessName.TRANSACTION_UPLOAD_RECEIPT]: null,
    [APIProcessName.TRANSACTION_DETACH_FINANCIAL_DOCUMENT]: null,
};

export const receiptMatchSources: HistorySourceTypeConfig[] =
    toPlatformTwinSources(RECEIPT_MATCH_PLATFORM_TWINS).map(
        ({ sourceType, platformTwins }) => ({
            sourceType,
            resourceTypeConfigs: [RECEIPT_MATCH_RESOURCE_CONFIG],
            groupBy: HistoryGroupBy.FIELD,
            platformTwins,
            enabled: true,
        }),
    );

export const financialDocumentDetachSource: HistorySourceTypeConfig = {
    sourceType: APIProcessName.FINANCIAL_DOCUMENT_DETACH_FINANCIAL_TRANSACTION,
    resourceTypeConfigs: [
        RECEIPT_MATCH_RESOURCE_CONFIG,
        ...DOCUMENT_RESOURCE_TYPE_CONFIGS,
    ],
    groupBy: HistoryGroupBy.FIELD,
    platformTwins: null,
    enabled: true,
};
